import React, { useEffect, useState, useContext } from "react";
import { useParams, Routes, Route } from "react-router-dom";
import { LoggerContext } from "context/LoggerContext";
import Module from "components/Module";
import Section from "./Section";
import { fetchApi } from "./../services/APIRequest";
import NotFound from "./NotFound";
import Loading from "./Loading";
import { useOrganization } from "context/OrganizationContext";
import { mapDynamicPageModules } from "helpers/mapDynamicPageModules";
import { dynamicModules } from "config/dynamicModules";
import MetaManager from "components/MetaManager";
import HeaderHtmlInjector from "components/HeaderHtmlInjector";
import FooterHtmlInjector from "components/FooterHtmlInjector";

const DynamicPage = ({ isStartPage = false, startPage = null, pageRoute, uniqueUrlParam = null, isModuleRoute = false, usePathModule = false, showListView = false, organizationOverride = null, orgUrlOverride = null }) => {
    const { logDebug, logError } = useContext(LoggerContext);
    const organizationFromContext = useOrganization();
    const organization = organizationOverride || organizationFromContext;
    const params = useParams();
    const { "*": wildcardParam, page_url } = params;
    
    // Enhanced URL extraction logic
    let currentPageUrl = uniqueUrlParam || page_url || wildcardParam;
    
    // If still no URL found, extract from current location
    if (!currentPageUrl || currentPageUrl.trim() === '') {
        const pathname = window.location.pathname;
        const segments = pathname.split('/').filter(Boolean);
        
        // For URLs like /museeum-weener/juedisches-leben/synagogenbrand
        // Extract the last segment as the page URL
        if (segments.length >= 1) {
            currentPageUrl = segments[segments.length - 1]; // 'synagogenbrand'
            logDebug('Extracted currentPageUrl from pathname:', {
                pathname,
                segments,
                extractedUrl: currentPageUrl
            });
        }
    }
    
    logDebug('DynamicPage URL resolution:', {
        uniqueUrlParam,
        page_url,
        wildcardParam,
        currentPageUrl,
        pageRoute,
        isModuleRoute,
        usePathModule,
        params: JSON.stringify(params)
    });
    
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setPage(null);
        setError(null);
        setLoading(true);

        if (isStartPage && startPage) {
            setPage(startPage);
            setLoading(false);
            return;
        }

        const loadPage = async () => {
            try {
                let apiEndpoint;
                let modConf;

                if (pageRoute) {
                    modConf = dynamicModules.find((dm) => dm.route === pageRoute);

                    if (modConf) {
                        logDebug('Loading module data:', { 
                            pageRoute,
                            moduleId: modConf.module_id,
                            apiMapping: modConf.apiMapping,
                            uniqueUrlParam,
                            showListView
                        });

                        // Special handling for checkout with cartToken
                        if (modConf.module_id === 9999 && uniqueUrlParam) {
                            // For checkout, use direct endpoint without pages/public
                            apiEndpoint = `checkout/${uniqueUrlParam}`;
                        }
                        // List view handling
                        else if (showListView || (!uniqueUrlParam && modConf.apiMapping)) {
                            apiEndpoint = `${modConf.apiMapping}/public`;
                        }
                        // Detail view handling
                        else if (uniqueUrlParam && modConf.apiMapping) {
                            apiEndpoint = `${modConf.apiMapping}/public/${uniqueUrlParam}`;
                        }
                        // Standard page handling
                        else {
                            apiEndpoint = `pages/public/${currentPageUrl}`;
                        }

                        logDebug('Using API endpoint:', apiEndpoint);

                        const response = await fetchApi(apiEndpoint, organization?.otk);
                        
                        if (!response) {
                            setError("No data found");
                            return;
                        }

                        const mappedData = mapDynamicPageModules(
                            modConf.module_id,
                            response,
                            "",
                            "",
                            pageRoute,
                            {
                                isModuleRoute,
                                usePathModule,
                                showListView
                            }
                        );

                        setPage(mappedData);
                    }                } else {
                    // Standard page loading logic
                    apiEndpoint = `pages/public/${currentPageUrl}`;
                    const response = await fetchApi(apiEndpoint, organization?.otk);
                    
                    logDebug('Standard page API response:', {
                        apiEndpoint,
                        response,
                        responseType: typeof response,
                        responseKeys: response ? Object.keys(response) : []
                    });
                    
                    if (!response) {
                        setError("Page not found");
                        return;
                    }

                    // Extract the actual page data from the response
                    const pageData = response.data || response;
                    setPage(pageData);
                }
            } catch (err) {
                logError('Error loading page:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (organization?.otk) {
            loadPage();
        } else {
            logDebug("Waiting for organization/OTK...");
        }
    }, [isStartPage, startPage, pageRoute, uniqueUrlParam, organization, currentPageUrl, isModuleRoute, usePathModule, showListView, logDebug, logError]);

    if (loading) return <Loading />;
    if (error) return <NotFound />;
    if (!page) return null;

    return (
        <>
            {page.header && <HeaderHtmlInjector htmlContent={page.header} />}
            <MetaManager title={page.title} />
            <div className="container-fluid g-0">
                <div className="row g-0">
                    <div className="col">
                        <Routes>
                            {page.sections?.filter(section => section.route)?.map((section, index) => (
                                <Route
                                    key={section.id ?? section.route ?? index}
                                    path={`${section.route}/*`}
                                    element={
                                        <Module module={section.global_module}>
                                            <Section organization={organization} section={section} />
                                        </Module>
                                    }
                                />
                            ))}
                            <Route
                                path="*"
                                element={
                                    <>
                                        {page.sections?.filter(section => !section.route)?.map((section, index) => (
                                            <Module 
                                                key={section.id ?? index} 
                                                module={section.global_module}
                                                ordered={true}
                                                orderDirection="asc"
                                                moduleRefId={section.module_ref_id}
                                                moduleFilter={section.module_filter}>
                                                <Section 
                                                    organization={organization} 
                                                    section={section} 
                                                />
                                            </Module>
                                        ))}
                                    </>
                                }
                            />
                        </Routes>
                    </div>
                </div>
            </div>
            {page.footer && <FooterHtmlInjector htmlContent={page.footer} />}
        </>
    );
};

export default DynamicPage;